/**
 * Quantities with their units (PROJECT-KNOWLEDGE.md §Formatting & copy).
 * Utilization is NEVER clamped: over 100% is the meaningful case.
 */

const group = (integerPart: string) => integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

function fixed(value: number, decimals: number): string {
  const negative = value < 0;
  const [whole = "0", fraction] = Math.abs(value).toFixed(decimals).split(".");
  const body = fraction ? `${group(whole)}.${fraction}` : group(whole);
  return negative ? `-${body}` : body;
}

/** One decimal only when fractional: `'12.5 loads'` / `'12 loads'`. */
export function formatLoads(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return `${formatLoadsValue(n)} loads`;
}

/** The bare number, same rule — for a grid cell that carries its unit in the header. */
export function formatLoadsValue(n: number): string {
  return Number.isInteger(n) ? fixed(n, 0) : fixed(n, 1);
}

/** Sales quantities: three decimals as the wire sends them, with thousands separators. */
export function formatQuantity(decimalString: string | number | null | undefined): string {
  if (decimalString === null || decimalString === undefined || decimalString === "") return "—";
  const n = typeof decimalString === "number" ? decimalString : Number(decimalString);
  if (!Number.isFinite(n)) return String(decimalString);
  return fixed(n, 3);
}

/** `0.62 → '62%'`, `1.37 → '137%'`. Never clamped, never a bar's business. */
export function formatUtilization(fraction: number | null | undefined): string {
  if (fraction === null || fraction === undefined || !Number.isFinite(fraction)) return "—";
  return `${Math.round(fraction * 100)}%`;
}

/** `'1,250.00 USD'` with a currency; bare `'1,250.00'` without (site costs carry none). */
export function formatMoney(
  amount: number | string | null | undefined,
  currency?: string | null,
): string {
  if (amount === null || amount === undefined || amount === "") return "—";
  const n = typeof amount === "number" ? amount : Number(amount);
  if (!Number.isFinite(n)) return String(amount);
  const body = fixed(n, 2);
  return currency ? `${body} ${currency}` : body;
}

/** LP service weights, separate from monetary costs. */
export function formatPenaltyPoints(amount: number, signed = false): string {
  if (!Number.isFinite(amount)) return "—";
  const body = fixed(amount, 2);
  return `${signed && amount > 0 ? "+" : ""}${body} points`;
}

/** `'1,200 mm'`. */
export function formatMm(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return `${Number.isInteger(n) ? fixed(n, 0) : fixed(n, 1)} mm`;
}

/** `'18,000 kg'`. */
export function formatKg(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return `${Number.isInteger(n) ? fixed(n, 0) : fixed(n, 1)} kg`;
}

/** `'3 d'`. */
export function formatDays(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return `${Number.isInteger(n) ? fixed(n, 0) : fixed(n, 1)} d`;
}

/** `'40 loads/day'`. */
export function formatLoadsPerDay(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return `${formatLoadsValue(n)} loads/day`;
}
